import { type JSX, useCallback, useEffect, useState } from 'react';

import { geraetAnlegen, geraete as ladeGeraete, geraetSperren, type Geraetezeile } from '../api/checkin';
import { standorte as ladeStandorte, type Planungsstandort } from '../api/tagesplanung';
import { ApiFehler } from '../api/fehler';
import { alsZeitpunkt } from '../datum';
import { markenklasse } from '../marken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Segmente } from '../komponenten/Segmente';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

type Umfang = 'alle' | 'aktiv' | 'gesperrt';

/**
 * Die Check-in-Tablets (TAG-49, TAG-60).
 *
 * Ein Tablet ist ein GERAET und kein Benutzer: Es gehoert zu einem Standort,
 * sieht nur dessen Termine des Tages und meldet sich mit Kennung und Schluessel
 * an. Der Schluessel erscheint hier GENAU EINMAL — danach traegt die Datenbank
 * nur seinen Streuwert (LEG-13). Wer ihn verliert, sperrt das Geraet und legt
 * ein neues an.
 *
 * Eingerichtet wird am Tablet selbst: `…/checkin.html` oeffnen, Kennung und
 * Schluessel eingeben. Den Kioskmodus (keine anderen Anwendungen, TAG-60)
 * stellt das Geraet ein, nicht diese Seite.
 *
 * SEITENTYP LISTE (ÄA-41): Zaehler im Seitenkopf, die Wahl „alle · aktiv ·
 * gesperrt" in der Filterleiste, die Tabelle in der Karte. Das Anlegen steht
 * in der Seitenleiste und nicht unter der Tabelle — und mit ihm der neue
 * Schluessel: Er erscheint dort, wo er eben angefordert wurde.
 */
export function Checkingeraete({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [liste, setzeListe] = useState<readonly Geraetezeile[] | null>(null);
  const [umfang, setzeUmfang] = useState<Umfang>('alle');
  const [standorte, setzeStandorte] = useState<readonly Planungsstandort[]>([]);
  const [kennung, setzeKennung] = useState('');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [standort, setzeStandort] = useState('');
  const [neu, setzeNeu] = useState<{ kennung: string; schluessel: string } | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  /* CRM-104: das Tablet, nach dessen Sperre das Pop-up gerade fragt. */
  const [zuSperren, setzeZuSperren] = useState<Geraetezeile | null>(null);

  const holen = useCallback(async () => {
    try {
      setzeListe(await ladeGeraete());
      setzeFehler(null);
    } catch (e) {
      setzeListe((alt) => alt ?? []);
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }, []);

  useEffect(() => {
    void holen();
    void ladeStandorte()
      .then(({ standorte: s }) => {
        setzeStandorte(s);
        setzeStandort((alt) => (alt === '' ? (s[0]?.kennung ?? '') : alt));
      })
      .catch(() => undefined);
  }, [holen]);

  /* ÄA-41: der Zaehler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(liste === null ? undefined : zaehlwort(liste.length, 'Tablet', 'Tablets'));

  const gezeigt = (liste ?? []).filter(
    (g) => umfang === 'alle' || (umfang === 'aktiv') === g.aktiv,
  );

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        filter={
          <Segmente
            beschriftung="Stand"
            gewaehlt={umfang}
            beiWahl={setzeUmfang}
            optionen={[
              { wert: 'alle', text: 'alle' },
              { wert: 'aktiv', text: 'aktiv' },
              { wert: 'gesperrt', text: 'gesperrt' },
            ]}
          />
        }
        hinweise={
          <p className="hilfe">
            Tablets für den Selbst-Check-in am Veranstaltungsort. Am Tablet die Seite{' '}
            <code>{`${window.location.origin}/checkin.html`}</code> öffnen und Kennung und Schlüssel
            eingeben.
          </p>
        }
        vorschauBeschriftung="Tablet anlegen"
        vorschau={
          darfPflegen ? (
            <>
              {/*
                LEG-13: Der Schluessel steht nur in diesem Zustand der Maske.
                Nach dem Neuladen ist er fort — die Datenbank kennt ihn nicht.
              */}
              {neu !== null && (
                <div className="hinweiskarte" role="status">
                  <h2>Tablet {neu.kennung} angelegt</h2>
                  <p>
                    Schlüssel: <code>{neu.schluessel}</code>
                  </p>
                  <p className="hilfe">
                    Er wird nur jetzt angezeigt. Geben Sie ihn am Tablet ein; danach lässt er sich
                    nicht mehr lesen.
                  </p>
                </div>
              )}

              {/* TAG-49 */}
              <form
                aria-label="Tablet anlegen"
                onSubmit={(e) => {
                  e.preventDefault();
                  geraetAnlegen({ kennung, bezeichnung, standort })
                    .then((r) => {
                      setzeNeu({ kennung, schluessel: r.schluessel });
                      setzeKennung('');
                      setzeBezeichnung('');
                      return holen();
                    })
                    .catch((f: unknown) => {
                      setzeFehler(f instanceof ApiFehler ? f : null);
                    });
                }}
              >
                <h2>Tablet anlegen</h2>
                <label className="feld">
                  <span className="feld__beschriftung">Kennung (klein, z. B. tablet-eingang)</span>
                  <input
                    value={kennung}
                    onChange={(e) => {
                      setzeKennung(e.target.value);
                    }}
                  />
                </label>
                <label className="feld">
                  <span className="feld__beschriftung">Bezeichnung</span>
                  <input
                    value={bezeichnung}
                    onChange={(e) => {
                      setzeBezeichnung(e.target.value);
                    }}
                  />
                </label>
                <label className="feld">
                  <span className="feld__beschriftung">Standort</span>
                  <select
                    value={standort}
                    onChange={(e) => {
                      setzeStandort(e.target.value);
                    }}
                  >
                    {standorte.map((s) => (
                      <option key={s.kennung} value={s.kennung}>
                        {s.bezeichnung}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="knopfreihe">
                  <button
                    type="submit"
                    className="knopf knopf--haupt"
                    disabled={kennung.trim() === '' || bezeichnung.trim() === '' || standort === ''}
                  >
                    Anlegen
                  </button>
                </div>
              </form>
            </>
          ) : undefined
        }
      >
        {liste === null && <p role="status">Wird geladen …</p>}
        {liste !== null && gezeigt.length === 0 && (
          <p className="liste__leer">
            {liste.length === 0
              ? 'Noch kein Tablet eingerichtet.'
              : umfang === 'aktiv'
                ? 'Kein Tablet ist aktiv.'
                : 'Kein Tablet ist gesperrt.'}
          </p>
        )}

        {gezeigt.length > 0 && (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Check-in-Tablets">
              <thead>
                <tr>
                  <th scope="col">Kennung</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Standort</th>
                  <th scope="col">Zuletzt gesehen</th>
                  <th scope="col">Stand</th>
                  {darfPflegen && <th scope="col">Handlung</th>}
                </tr>
              </thead>
              <tbody>
                {gezeigt.map((g) => (
                  <tr key={g.id} className={g.aktiv ? undefined : 'liste__zeile--blass'}>
                    <th scope="row" className="liste__nummer">{g.kennung}</th>
                    <td className="liste__name">{g.bezeichnung}</td>
                    <td>{g.standort}</td>
                    <td className="liste__einzeilig">{alsZeitpunkt(g.zuletztGesehenAm) ?? '—'}</td>
                    <td>
                      {/* Gesperrt ist endgueltig: Wer den Schluessel verlor, legt ein neues Tablet an. */}
                      <span className={markenklasse(g.aktiv ? 'laufend' : 'sackgasse')}>
                        {g.aktiv ? 'aktiv' : 'gesperrt'}
                      </span>
                    </td>
                    {darfPflegen && (
                      <td>
                        {g.aktiv && (
                          <button
                            type="button"
                            className="knopf knopf--klein knopf--eingriff"
                            onClick={() => setzeZuSperren(g)}
                          >
                            Sperren …
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {/*
        TAG-49, LEG-13: Die Sperre ist endgültig — der Schlüssel ist nur als
        Streuwert bekannt, und ein gesperrtes Tablet wird nicht wieder aktiv.
        Scheitert die Sperre, wirft sie: Der Fehler steht im Pop-up. Ein Fehler
        beim Neuladen danach steht auf der Seite (`holen`).
      */}
      {zuSperren !== null && (
        <Rueckfrage
          frage="Tablet sperren?"
          unterzeile={`${zuSperren.kennung} · ${zuSperren.bezeichnung}`}
          tat="Sperren"
          beiBestaetigen={async () => {
            await geraetSperren(zuSperren.id);
            setzeZuSperren(null);
            await holen().catch(() => undefined);
          }}
          beiSchliessen={() => setzeZuSperren(null)}
        >
          <p>
            Das Tablet am Standort {zuSperren.standort} nimmt sofort keinen Check-in mehr an. Die
            Sperre lässt sich nicht zurücknehmen — für weiteren Betrieb legen Sie ein neues
            Tablet an.
          </p>
        </Rueckfrage>
      )}
    </>
  );
}
